import React from 'react';
import ThaiDatePicker, { ThaiDatePickerProps } from './ThaiDatePicker';

export interface BuddhistEraDatePickerProps extends Omit<ThaiDatePickerProps, 'value' | 'onChange'> {
  /**
   * Date value in ISO format (AD/Gregorian calendar)
   * e.g., "2024-11-25" or Date object
   */
  value?: string | Date | null;
  
  /**
   * Callback when date changes
   * Returns ISO date string in AD format (e.g., "2024-11-25")
   */
  onChange?: (value: string | null) => void;
  
  /**
   * Minimum date in ISO format (AD)
   */
  minDate?: Date;
  
  /**
   * Maximum date in ISO format (AD)
   */
  maxDate?: Date;
}

/**
 * Buddhist Era Date Picker Component
 * 
 * Wraps ThaiDatePicker to handle Buddhist Era (BE) to Gregorian (AD) conversion.
 * - Displays dates in Buddhist Era format (BE = AD + 543)
 * - Accepts input in Buddhist Era format
 * - Stores dates in Gregorian calendar format (AD) for database compatibility
 * 
 * Edge cases handled:
 * - Null dates: Returns null without errors
 * - Invalid dates: Returns null for invalid date strings
 * - Leap years: Properly handles Feb 29 in leap years
 * - Year boundaries: Correctly converts dates at year start/end
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
const BuddhistEraDatePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  ...otherProps
}: BuddhistEraDatePickerProps) => {
  // Convert string value to Date object for ThaiDatePicker
  // Handles edge cases: null, invalid dates, various formats
  const dateValue = React.useMemo(() => {
    // Edge case: null or undefined value
    if (!value) return null;
    
    // Edge case: already a Date object
    if (value instanceof Date) {
      // Validate the date is not invalid
      return isNaN(value.getTime()) ? null : value;
    }
    
    // Parse ISO date string (AD format) to Date object
    // Edge case: invalid date strings
    const parsed = new Date(value);
    
    // Edge case: invalid date (e.g., "2024-13-01", "invalid-date")
    if (isNaN(parsed.getTime())) {
      return null;
    }
    
    return parsed;
  }, [value]);

  // Handle date change from ThaiDatePicker
  // Converts Date object to ISO string in AD format for storage
  const handleDateChange = React.useCallback((date: Date | null) => {
    if (!onChange) return;
    
    // Edge case: null date (user cleared the field)
    if (!date) {
      onChange(null);
      return;
    }
    
    // Edge case: invalid date object
    if (isNaN(date.getTime())) {
      onChange(null);
      return;
    }
    
    // Convert Date object to ISO string (AD format) for storage
    // Format: YYYY-MM-DD
    // This ensures consistent storage format regardless of timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const isoString = `${year}-${month}-${day}`;
    
    onChange(isoString);
  }, [onChange]);

  return (
    <ThaiDatePicker
      value={dateValue}
      onChange={handleDateChange}
      minDate={minDate}
      maxDate={maxDate}
      {...otherProps}
    />
  );
};

export default BuddhistEraDatePicker;
